//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import CloseIcon from '@mui/icons-material/Close'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import { DateRange, DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import React, { ReactElement, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { CompanyProfileCTO, OPEN_TRADE_UUID } from '@shared/CompanyProfileCTO'

import { PoolOffchainDataLenderReportingSchema, PoolOffchainDataReportingSchema } from '@shared/PoolOffchainDataCTO'
import { PoolReportingAccountCTOSchema, ReportDataShape, ReportDefinitionCTO } from '@shared/ReportDefinitionCTO'
import { EthAddr } from '@shared/SchemaTypes'

import { modalStyle } from '@react/components/ButtonModal'
import { columnFromSchema } from '@react/components/pools/ExplorerColumn'
import { reportDefinitionPut, reportDefinitionsForShape } from '@react/fetchers/reportDefinitionFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { RoleEnum } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import * as yup from 'yup'
import {
  PoolEventSchema,
  PoolFlexOverviewCTOSchema,
  PoolLenderReportingAccountCTOSchema,
  PoolLenderReportingAccountFlexCTOSchema,
  PoolOverviewCTOSchema,
} from '@shared/sharedTypes'
import { PoolDynamicOverviewCTOSchema } from '@shared/PoolDynamicCTO'
import { PoolLenderReportingAccountDynamicCTOSchema } from '@shared/PoolDynamicAccountCTO'

dayjs.extend(utc)
dayjs.extend(timezone)

export const LenderExplorerSchema = yup.object({
  poolAccountCTO: PoolReportingAccountCTOSchema(),
  poolOffchainDataCTO: PoolOffchainDataReportingSchema(),
})

export const LenderExplorerSchemaLenderView = yup.object({
  poolAccountCTO: PoolLenderReportingAccountCTOSchema(),
  poolOffchainDataCTO: PoolOffchainDataLenderReportingSchema(),
})

export const LenderExplorerSchemaLenderViewFlex = yup.object({
  poolAccountCTO: PoolLenderReportingAccountFlexCTOSchema(),
  poolOffchainDataCTO: PoolOffchainDataLenderReportingSchema(),
})

export const LenderExplorerSchemaLenderViewDynamic = yup.object({
  poolAccountCTO: PoolLenderReportingAccountDynamicCTOSchema(),
  poolOffchainDataCTO: PoolOffchainDataLenderReportingSchema(),
})

export default function ReportExplorer(props: {
  loading: boolean
  reportDataShape: ReportDataShape
  rows: GridRowsProp
  dateRange?: [Dayjs, Dayjs]
  setDateRange?: (dateRange: [Dayjs, Dayjs]) => void
  lenderAddr?: EthAddr
  setLenderAddr?: (lenderAddr: EthAddr | undefined) => void
  poolAddr?: EthAddr
  setPoolAddr?: (poolAddr: EthAddr | undefined) => void
  defaultColumnNames?: string[]
  defaultSortModel?: GridSortModel
  lenderAddrs?: EthAddr[]
  poolAddrs?: EthAddr[]
  summaryRows?: GridRowsProp
  refreshData?: () => void
  fetchDataError: Error | undefined
  sx?: SxProps
}): React.ReactElement {
  const {
    lenderAddrs,
    poolAddrs,
    lenderAddr,
    setLenderAddr,
    poolAddr,
    setPoolAddr,
    rows,
    dateRange,
    setDateRange,
    reportDataShape,
    defaultColumnNames,
    defaultSortModel,

    refreshData,
  } = props

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { etherscanUrl },
    userConnection: { accountAddr, roleEnum },
  } = useOtChainState()

  const poolOverviewCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const [columns, setColumns] = useState<GridColDef[]>([])
  const companyProfile: CompanyProfileCTO = {
    uuid: OPEN_TRADE_UUID,
    companyName: 'Open Trade',
    wallets: [],
    emails: [],
  }

  useEffect(() => {
    const reportPoolAddr = localStorage.getItem('reportPoolAddr') as EthAddr | undefined

    if (reportPoolAddr && setPoolAddr && poolAddrs && poolAddrs.includes(reportPoolAddr)) {
      setPoolAddr(reportPoolAddr)
    }
  }, [setPoolAddr, poolAddrs])

  useEffect(() => {
    const reportLenderAddr = localStorage.getItem('reportLenderAddr') as EthAddr | undefined
    if (reportLenderAddr && setLenderAddr && lenderAddrs && lenderAddrs.includes(reportLenderAddr)) {
      roleEnum === RoleEnum.LENDER ? setLenderAddr(accountAddr) : setLenderAddr(reportLenderAddr)
    }
  }, [setLenderAddr, lenderAddrs])

  useEffect(() => {
    const columnVisibilityString = localStorage.getItem('reportColumnVisibilityModel') as string | undefined
    if (columns && columnVisibilityString) {
      const columnVisibilityModel = JSON.parse(columnVisibilityString)
      setColumnVisibilityModel(columnVisibilityModel)
    }
  }, [columns])

  const [reportDefinition, setReportDefinition] = useState<ReportDefinitionCTO | undefined>(undefined)
  const [reportDefinitions, setReportDefinitions] = useState<ReportDefinitionCTO[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean> | undefined>(undefined)
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(undefined)

  const allPropertyList = (prefix: string, fields: Record<string, yup.SchemaDescription>): string[] => {
    const keys = Object.keys(fields)
    const list: string[] = []
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const field = fields[key]
      if (field.type === 'object') {
        const subList = allPropertyList(
          prefix + key + '.',
          (field as yup.SchemaObjectDescription).fields as Record<string, yup.SchemaDescription>
        )
        list.push(...subList)
      } else {
        list.push(prefix + key)
      }
    }
    return list
  }
  const handleSetColumnVisibilityModel = (newModel: Record<string, boolean> | undefined) => {
    if (newModel) {
      const str = JSON.stringify(newModel)

      localStorage.setItem('reportColumnVisibilityModel', str)
    }
    setColumnVisibilityModel(newModel)
  }
  const handlePoolAddrChange = (event: SelectChangeEvent) => {
    const poolAddr = event.target.value as EthAddr
    if (setPoolAddr) {
      setPoolAddr(poolAddr)
      localStorage.setItem('reportPoolAddr', poolAddr)
    }
  }

  useEffect(() => {
    const asyncFunction = async () => {
      let _reportDefinitions: ReportDefinitionCTO[] = []
      if (serverApiBaseUrl) {
        try {
          _reportDefinitions = await reportDefinitionsForShape(
            serverApiBaseUrl,
            restAPIKey,
            companyProfile?.uuid,
            reportDataShape
          )
        } catch {
          logOT.error('Error fetching report shape, using default.')
        }
        setReportDefinitions(_reportDefinitions)
        let reportDefinitionDefault = _reportDefinitions.find((r) => r.defaultForShape)

        if (!reportDefinitionDefault) {
          reportDefinitionDefault = {
            isGlobal: false,
            reportUuid: uuidv4(),
            timestamp: Date.now(),
            companyUuid: OPEN_TRADE_UUID,
            reportName: 'Default',
            defaultForShape: true,
            dataShape: reportDataShape,
            xGridDefinition: undefined,
          }
        }
        setReportDefinitions(_reportDefinitions.length === 0 ? [reportDefinitionDefault] : _reportDefinitions)
        setReportDefinition(reportDefinitionDefault)

        let _schema: yup.ObjectSchema<any> | undefined = undefined

        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_TERM) {
          _schema = LenderExplorerSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_TERM_LENDER) {
          _schema = LenderExplorerSchemaLenderView
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_FLEX) {
          _schema = LenderExplorerSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_FLEX_LENDER) {
          _schema = LenderExplorerSchemaLenderViewFlex
        }

        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC) {
          _schema = LenderExplorerSchemaLenderViewDynamic
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC_LENDER) {
          _schema = LenderExplorerSchemaLenderViewDynamic
        }

        if (reportDefinitionDefault?.dataShape === ReportDataShape.POOL_EVENTS) {
          _schema = PoolEventSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.POOL_EVENTS_LENDER) {
          _schema = PoolEventSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.POOL_OVERVIEW_FLEX) {
          _schema = PoolFlexOverviewCTOSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.POOL_OVERVIEW_DYNAMIC) {
          _schema = PoolDynamicOverviewCTOSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.POOL_OVERVIEW_TERM) {
          _schema = PoolOverviewCTOSchema
        }
        if (reportDefinitionDefault?.dataShape === ReportDataShape.PERFORMANCE) {
          _schema = PoolOverviewCTOSchema
        }
        if (!_schema) {
          throw new Error('No schema found')
        } else {
          const cols: GridColDef[] = []

          const list: string[] = allPropertyList('', _schema.describe().fields as Record<string, yup.SchemaDescription>)

          let _columnVisibilityModel: Record<string, boolean> | undefined = undefined
          if (defaultColumnNames) {
            if (!reportDefinitionDefault?.xGridDefinition) {
              _columnVisibilityModel = {}
              setSortModel(defaultSortModel)
            }
            defaultColumnNames.forEach((prop) => {
              if (_columnVisibilityModel) {
                _columnVisibilityModel[prop] = true
              }
              if (_schema) {
                cols.push(columnFromSchema(_schema, prop, etherscanUrl))
              }
            })
          }

          list.forEach((prop) => {
            if (defaultColumnNames) {
              if (_schema && !defaultColumnNames.includes(prop)) {
                if (_columnVisibilityModel) {
                  _columnVisibilityModel[prop] = false
                }
                cols.push(columnFromSchema(_schema, prop, etherscanUrl))
              }
            } else {
              if (_schema) {
                cols.push(columnFromSchema(_schema, prop, etherscanUrl))
              }
            }
          })
          if (!defaultSortModel) {
            const timestampColumn = cols.find((col) => col.field.endsWith('timestamp'))
            if (timestampColumn) {
              const timestampSortModel: GridSortModel = [{ field: timestampColumn.field, sort: 'desc' }]
              setSortModel(timestampSortModel)
            }
          }

          setColumns(cols)
          handleSetColumnVisibilityModel(_columnVisibilityModel)
        }
      }
    }
    asyncFunction().catch((e) => {
      logOT.error('Error getting columns', e)
    })
  }, [serverApiBaseUrl])

  function CustomToolbar(): ReactElement {
    const [openModal, setOpenModal] = React.useState(false)
    const [reportName, setReportName] = useState<string>('')

    const theme = useTheme()
    useEffect(() => {
      window.addEventListener(
        'modalClose',
        () => {
          setOpenModal(false)
        },
        false
      )

      return () => {
        window.removeEventListener(
          'modalClose',
          () => {
            setOpenModal(false)
          },
          false
        )
      }
    }, [])

    const apiRef = useGridApiContext()
    const handleReportChange = (event: SelectChangeEvent) => {
      const asyncFunction = async () => {
        const reportUuid = event.target.value as string

        const _reportDefinition = reportDefinitions.find((r) => r.reportUuid === reportUuid)
        setReportDefinition(_reportDefinition)

        if (_reportDefinition?.xGridDefinition) {
          apiRef.current.restoreState(_reportDefinition.xGridDefinition)
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error getting columns', e)
      })
    }
    const handleSaveReport = () => {
      const asyncFunction = async () => {
        const state = apiRef.current.exportState()
        const newReportDefinition = {
          ...reportDefinition,
          xGridDefinition: state,
        }

        if (serverApiBaseUrl) {
          await reportDefinitionPut(serverApiBaseUrl, restAPIKey, newReportDefinition).catch((e: any) => {
            logOT.error('Error saving report', e)
          })
          const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
            serverApiBaseUrl,
            restAPIKey,
            companyProfile?.uuid,
            reportDataShape
          )
          setReportDefinitions(_reportDefinitions)
          handleClose()
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error saving report', e)
      })
    }
    const handleSaveNewReport = () => {
      const asyncFunction = async () => {
        const state = apiRef.current.exportState()

        const newReportDefinition: ReportDefinitionCTO = {
          isGlobal: false,
          reportName: reportName,
          reportUuid: uuidv4(),
          timestamp: Date.now(),
          dataShape: reportDefinition?.dataShape ?? ReportDataShape.UNKNOWN,
          defaultForShape: false,
          companyUuid: OPEN_TRADE_UUID,
          xGridDefinition: state,
        }

        if (serverApiBaseUrl) {
          await reportDefinitionPut(serverApiBaseUrl, restAPIKey, newReportDefinition).catch((e: any) => {
            logOT.error('Error saving report', e)
          })
          const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
            serverApiBaseUrl,
            restAPIKey,
            companyProfile?.uuid,
            reportDataShape
          )
          setReportDefinitions(_reportDefinitions)
          handleClose()
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error saving report', e)
      })
    }
    const handleClose = (): void => {
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    }
    const localSetDateRange = (newDateRange: DateRange<Dayjs>) => {
      logOT.info('localSetDateRange', newDateRange[0], newDateRange[1])
      if (setDateRange && newDateRange[0] && newDateRange[1]) {
        setDateRange([newDateRange[0], newDateRange[1]])
      }
    }

    return (
      <Box sx={props.sx}>
        <GridToolbarContainer data-testid="GridToolbarContainer1" style={{ float: 'right', padding: '15px' }}>
          {setLenderAddr && roleEnum !== RoleEnum.LENDER && lenderAddrs && (
            <FormControl sx={{ m: 1, width: 200 }}>
              <InputLabel id="lender-label">Lender</InputLabel>
              <Select
                style={{ width: 200 }}
                labelId="lender-label"
                id="lender-select"
                input={<OutlinedInput id="lender-select" label="Lender" />}
                value={lenderAddr ?? ('' as string)}
                renderValue={(value: string) => {
                  return (
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      {value}
                    </Typography>
                  )
                }}
                onChange={(event: SelectChangeEvent) => {
                  setLenderAddr(event.target.value as EthAddr)
                }}
              >
                {lenderAddrs.map((value) => {
                  return (
                    <MenuItem
                      value={value}
                      key={`${value}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {value}
                      </Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          )}
          {setPoolAddr && poolAddrs && (
            <FormControl sx={{ m: 1, width: 200 }}>
              <InputLabel id="pool-label">Vault</InputLabel>
              <Select
                style={{ width: 200 }}
                labelId="pool-label"
                id="pool-select"
                input={<OutlinedInput id="pool-select" label="Pool" />}
                value={poolAddr ?? ('' as string)}
                renderValue={(value: string) => {
                  return (
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      {poolOverviewCTOs[value].name}
                    </Typography>
                  )
                }}
                onChange={(event: SelectChangeEvent) => {
                  handlePoolAddrChange(event)
                }}
              >
                {poolAddrs.map((value) => {
                  return (
                    <MenuItem
                      value={value}
                      key={`${value}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {poolOverviewCTOs[value].name}
                      </Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          )}
          {dateRange && setDateRange ? (
            <Box sx={{ flexGrow: 0, width: 275 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="flex flex-col">
                  <DateRangePicker
                    label={'Date Range'}
                    value={dateRange}
                    onChange={(newValue) => localSetDateRange(newValue)}
                  />
                </div>
              </LocalizationProvider>
            </Box>
          ) : null}
          {refreshData ? (
            <Box sx={{ flexGrow: 0 }}>
              <Button
                disabled={props.loading}
                sx={{
                  backgroundColor: theme.palette.fg.main,
                  color: '#FFFFFF',
                  padding: '11px 16px 11px 16px',
                  marginLeft: '8px',
                  borderRadius: '6px',
                }}
                onClick={() => {
                  refreshData()
                }}
                variant={'contained'}
              >
                <Typography variant={'buttonBold'}>Fetch Data</Typography>
              </Button>
            </Box>
          ) : null}
        </GridToolbarContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            marginTop: 2,
            alignItems: 'top',
            padding: '0 15px 10px 17px',
          }}
        >
          <Box sx={{ flexGrow: 0, width: 200, marginRight: '15px' }}>
            <FormControl fullWidth>
              <InputLabel id="select-report">Report</InputLabel>
              <Select
                labelId="select-report"
                id="select-report"
                value={reportDefinition?.reportUuid ?? ''}
                label="Report"
                onChange={handleReportChange}
              >
                {reportDefinitions.map((r) => (
                  <MenuItem key={r.reportUuid} value={r.reportUuid}>
                    {r.reportName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Button
              sx={{
                marginLeft: '4px',
                height: '40px',
                textTransform: 'none',
                marginRight: '15px',
                color: '#171717',
                backgroundColor: '#EEEEEE',
              }}
              variant="contained"
              onClick={() => {
                setReportName('')
                setOpenModal(true)
              }}
            >
              Save Report Definition
            </Button>
            <GridToolbarFilterButton
              slotProps={
                {
                  button: {
                    startIcon: <FilterAltOutlinedIcon />,
                    variant: 'contained',
                    style: {
                      height: '40px',
                      marginRight: '15px',
                      color: '#171717',
                      textTransform: 'none',
                      backgroundColor: '#EEEEEE',
                    },
                  },
                } as any
              }
            />
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  style: {
                    marginRight: '15px',
                  },
                },
              }}
            />
            <GridToolbarExport
              csvOptions={{}}
              slotProps={{
                button: {
                  variant: 'contained',
                  style: {
                    height: '40px',
                    marginRight: '15px',
                    color: '#171717',
                    textTransform: 'none',
                    backgroundColor: '#EEEEEE',
                  },
                },
              }}
            />
            {props.fetchDataError && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignContent: 'center',
                  margin: '8px',
                }}
              >
                <Typography textAlign={'center'} variant="bodyMuted">
                  Error Fetching Data {props.fetchDataError.message}
                </Typography>
              </Box>
            )}
            {props.loading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignContent: 'center',
                  margin: '8px',
                }}
              >
                <Box>
                  <CircularProgress />
                </Box>
                <Typography textAlign={'center'} variant="bodyMuted">
                  Loading Report Data
                </Typography>
              </Box>
            )}

            <Modal
              open={openModal}
              data-testid="modal"
              onClose={() => setOpenModal(false)}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Paper sx={modalStyle}>
                <Grid alignItems={'stretch'} direction={'row'} container spacing={8} sx={{ flexGrow: 1 }}>
                  <Grid key="1" item xs={12}>
                    <Typography
                      align="left"
                      sx={{ display: 'inline', float: 'left', fontWeight: 'bold', marginLeft: 2 }}
                      variant={'h4'}
                    >
                      Save Report Definition
                    </Typography>

                    <IconButton
                      onClick={handleClose}
                      sx={{
                        height: '14px',
                        width: '14px',
                        padding: 0,
                        float: 'right',
                        color: theme.palette.primary.main,
                      }}
                    >
                      <CloseIcon sx={{ width: '12px', height: '12px', marginTop: 0, color: theme.palette.fg.muted }} />
                    </IconButton>
                  </Grid>
                  <Grid key="2" item xs={6}>
                    <Typography>{reportDefinition?.reportName}</Typography>
                  </Grid>
                  <Grid key="3" item xs={6}>
                    <Button variant="contained" onClick={handleSaveReport}>
                      Save
                    </Button>
                  </Grid>
                  <Grid key="4" item xs={6}>
                    <TextField
                      id="outlined-controlled"
                      label="New Report Name"
                      value={reportName}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setReportName(event.target.value)
                      }}
                    />
                  </Grid>
                  <Grid key="5" item xs={6}>
                    <Button disabled={reportName.length === 0} variant="contained" onClick={handleSaveNewReport}>
                      Save new
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Modal>
          </Box>
        </Box>
      </Box>
    )
  }

  const modifiedRows = rows?.map((row) => ({
    ...row,
    assets: isNaN(row?.assets) ? 0 : row.assets,
  }))

  const sumErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))
        .toString()

      return sum
    },
    label: 'Sum ERC20',
    columnTypes: ['erc20'],
  }
  const aveErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))

      const ave: bigint = vals.length > 0 ? sum / BigInt(vals.length) : BigInt(0)
      return ave.toString()
    },
    label: 'Ave ERC20',
    columnTypes: ['erc20'],
  }

  if (reportDefinition?.xGridDefinition && columns.length > 0) {
    return (
      <DataGridPremium
        rows={modifiedRows}
        columns={columns}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleSetColumnVisibilityModel}
        onSortModelChange={(newModel) => setSortModel(newModel)}
        initialState={reportDefinition.xGridDefinition}
        sx={{ fontSize: 12, fontWeight: 500 }}
        autoHeight
        slotProps={{ toolbar: { csvOptions: { allColumns: true } } }}
        slots={{
          toolbar: CustomToolbar,
          openFilterButtonIcon: FilterAltOutlinedIcon,
        }}
        getDetailPanelHeight={() => 'auto'}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          sumErc20: sumErc20Aggregation,
          aveErc20: aveErc20Aggregation,
        }}
      />
    )
  }
  if (columns.length > 0) {
    return (
      <DataGridPremium
        rows={modifiedRows}
        columns={columns}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleSetColumnVisibilityModel}
        onSortModelChange={(newModel) => setSortModel(newModel)}
        sx={{ fontSize: 12, fontWeight: 500 }}
        autoHeight
        slots={{
          toolbar: CustomToolbar,
          openFilterButtonIcon: FilterAltOutlinedIcon,
        }}
        getDetailPanelHeight={() => 'auto'}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          sumErc20: sumErc20Aggregation,
          aveErc20: aveErc20Aggregation,
        }}
      />
    )
  }
  return <div>Loading...</div>
}
